/**
 * Page : Liste des formations avec accès à leurs questionnaires.
 * Route : /:tenant/dashboard/formation-questionnaire
 *
 * L'admin sélectionne une formation (audit) dans la liste déroulante,
 * puis le composant FormationQuestionnaireManager s'affiche pour gérer
 * ses questionnaires Pré/Post/Intermédiaire.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';

import { useAuth } from '../../../hooks/use-auth';
import { userApi } from '../../../api/user-api';
import FormationQuestionnaireManager from '../../../components/dashboard/formation-questionnaire/formation-questionnaire-manager';

const FormationQuestionnairePage = () => {
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [selectedAuditId, setSelectedAuditId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAudits = useCallback(async () => {
    try {
      const data = await userApi.getEppByType(user.admin_id, 'audit');
      setAudits(Array.isArray(data) ? data : []);
    } catch {
      setError('Impossible de charger la liste des formations.');
    } finally {
      setLoading(false);
    }
  }, [user.admin_id]);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
      <Container maxWidth="lg">
        {/* Titre */}
        <Stack direction="row" spacing={1.5} alignItems="center" mb={4}>
          <QuizIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Questionnaires de Formation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gérez les questionnaires Pré-Formation, Post-Formation et les évaluations intermédiaires.
            </Typography>
          </Box>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Sélection de la formation */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel>Sélectionner une formation</InputLabel>
              <Select
                value={selectedAuditId}
                label="Sélectionner une formation"
                onChange={(e) => setSelectedAuditId(e.target.value)}
              >
                {audits.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedAuditId ? (
              <FormationQuestionnaireManager auditId={selectedAuditId} />
            ) : (
              <Alert severity="info">
                Sélectionnez une formation pour gérer ses questionnaires.
              </Alert>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

FormationQuestionnairePage.getLayout = (page) => page;

export default FormationQuestionnairePage;
